const GLOBAL_DIALOG_BACKDROP_SELECTORS = [
    '#history-modal.active',
    '#saved-modal.active',
    '#stats-modal.active',
    '#theme-modal.active',
    '#settings-modal.active',
    '#memora-ai-settings-modal.active',
    '#api-key-modal.active',
    '#quiz-gen-modal.active',
    '#pdf-tutor-modal.active',
    '#pdf-tutor-entry-choice-modal.active',
    '#pdf-tutor-start-progress-modal.active',
    '#canvas-settings-modal.active',
    '#canvas-browser-modal.active',
    '#canvas-help-modal.active',
    '#conversion-progress-modal.active',
    '#database-modal.active',
    '#export-modal.active',
    '#slide-digest-modal.active',
    // Confirmations sit over the app as it is; they do not dim it (modals.css).
    '#session-name-modal.active',
    '#srs-rating-modal.active',
    '#adaptive-snapshot-modal.active',
    '#calendar-import-review-modal.active',
    '#import-modal.active',
    '#quiz-gen-prompt-expanded-modal',
    '#pdf-tutor-script-prompt-modal',
    '#ai-expl-regen-prompt-modal',
    '#section-editor-modal.active',
    '#regen-confirm-modal',
    '#memory-saver-modal',
    '#quiz-condense-modal',
    '#quiz-condense-progress-modal',
    '#generated-quizzes-overlay',
    '#temp-popup-overlay',
    '#quiz-gen-cancel-confirm',
    '#save-quiz-popup',
    '#manual-help-popup',
    '#public-auth-overlay.active',
    '#public-tour-card.active',
    '#public-tour-overlay:not([hidden])',
    '.ai-popup-modal.active',
    '.ai-expl-slide-modal.active',
    '.mystery-card-overlay.active'
].join(',');

/* Every element that can ever be a backdrop candidate, whatever its state: the selectors above
 * with their state filters removed. */
const GLOBAL_DIALOG_BACKDROP_ANY_SELECTOR = GLOBAL_DIALOG_BACKDROP_SELECTORS
    .split(',')
    .map((selector) => selector.replace(/\.active\b/g, '').replace(/:not\(\[hidden\]\)/g, '').trim())
    .filter(Boolean)
    .join(',');

let globalDialogBackdropStateRaf = 0;
let globalDialogBackdropObserverInstalled = false;

/* The observer watches the whole body, so text layers, streaming answers and per-frame animation
 * styles used to queue a full visibility pass (computed style and layout reads for every dialog)
 * on nearly every frame. Only a change that can move a candidate's visibility needs one: on body
 * or html, on or inside a candidate, or on an ancestor of one. */
function isGlobalDialogBackdropRelevantNode(node) {
    if (!(node instanceof Element)) return false;
    if (node === document.body || node === document.documentElement) return true;
    if (node.closest(GLOBAL_DIALOG_BACKDROP_ANY_SELECTOR)) return true;
    return !!node.querySelector(GLOBAL_DIALOG_BACKDROP_ANY_SELECTOR);
}

function isGlobalDialogBackdropRelevantMutation(record, targets = null) {
    // classList.add of a class already present, and every same-value setAttribute, still queue a
    // record. On body that re-ran the visibility pass on every frame some script re-asserted a class.
    if (record.type === 'attributes' && record.oldValue === record.target.getAttribute(record.attributeName)) return false;
    /* One batch can hold a record per row appended to the same parent. Reopening a long Voice Chat
     * appended a thousand rows and searched the whole feed once for each, so a target's answer is
     * reused within the batch. */
    let relevant = targets?.get(record.target);
    if (relevant === undefined) {
        relevant = isGlobalDialogBackdropRelevantNode(record.target);
        targets?.set(record.target, relevant);
    }
    if (relevant) return true;
    if (record.type !== 'childList') return false;
    return [...record.addedNodes, ...record.removedNodes].some((node) => (
        node instanceof Element
        && (node.matches(GLOBAL_DIALOG_BACKDROP_ANY_SELECTOR) || !!node.querySelector(GLOBAL_DIALOG_BACKDROP_ANY_SELECTOR))
    ));
}

function isBackdropCandidateVisible(el) {
    if (!el || el.hidden || !document.body.contains(el)) return false;
    if (el.matches('[data-saved-embedded], .memora-embedded-setup')) return false;
    const style = window.getComputedStyle(el);
    return el.getClientRects().length > 0
        && style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number(style.opacity || 1) > 0.01;
}

function refreshGlobalDialogBackdropState() {
    globalDialogBackdropStateRaf = 0;
    const shouldDimWorkArea = Array.from(document.querySelectorAll(GLOBAL_DIALOG_BACKDROP_SELECTORS))
        .some(isBackdropCandidateVisible);
    document.body.classList.toggle('modal-backdrop-active', shouldDimWorkArea);
}

function queueGlobalDialogBackdropStateUpdate() {
    if (globalDialogBackdropStateRaf) return;
    globalDialogBackdropStateRaf = window.requestAnimationFrame(refreshGlobalDialogBackdropState);
}

function installGlobalDialogBackdropObserver() {
    if (globalDialogBackdropObserverInstalled || !document.body) return;
    globalDialogBackdropObserverInstalled = true;

    const observer = new MutationObserver((records) => {
        if (globalDialogBackdropStateRaf) return;
        const targets = new Map();
        if (records.some((record) => isGlobalDialogBackdropRelevantMutation(record, targets))) queueGlobalDialogBackdropStateUpdate();
    });

    observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['class', 'style', 'hidden']
    });

    const queueForRelevantEvent = (event) => {
        if (!globalDialogBackdropStateRaf && isGlobalDialogBackdropRelevantNode(event.target)) {
            queueGlobalDialogBackdropStateUpdate();
        }
    };
    document.addEventListener('transitionend', queueForRelevantEvent, true);
    document.addEventListener('animationend', queueForRelevantEvent, true);
    window.addEventListener('resize', queueGlobalDialogBackdropStateUpdate, { passive: true });
    // Safety net: anything the student does gets one check on the next frame.
    document.addEventListener('click', queueGlobalDialogBackdropStateUpdate, { capture: true, passive: true });
    document.addEventListener('keydown', queueGlobalDialogBackdropStateUpdate, { capture: true, passive: true });

    queueGlobalDialogBackdropStateUpdate();
}

let aiExplSlideModalEscBound = false;

function closeAiExplSlideModal() {
    const modal = $('ai-expl-slide-modal');
    if (!modal || !modal.classList.contains('active')) return;

    modal.classList.remove('active');
    const modalContent = modal.querySelector('.ai-expl-slide-modal-content');
    if (modalContent) modalContent.innerHTML = '';
    document.body.classList.remove('ai-expl-slide-modal-open');
    if (typeof scheduleQuickActionsDockPosition === 'function') scheduleQuickActionsDockPosition();
}

function ensureAiExplSlideModal() {
    let modal = $('ai-expl-slide-modal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'ai-expl-slide-modal';
    modal.className = 'ai-expl-slide-modal';
    modal.setAttribute('data-testid', 'pdf-explanation-modal');
    modal.innerHTML = `
        <div class="ai-expl-slide-modal-card" data-testid="pdf-explanation-modal-card" role="dialog" aria-modal="true" aria-label="Expanded slide explanation">
            <button type="button" class="ai-expl-slide-modal-close" data-testid="pdf-explanation-modal-close" aria-label="Close expanded slide">&times;</button>
            <div class="ai-expl-slide-modal-content hide-scrollbar" data-testid="pdf-explanation-modal-content"></div>
        </div>
    `;
    const closeBtn = modal.querySelector('.ai-expl-slide-modal-close');
    if (closeBtn) closeBtn.addEventListener('click', closeAiExplSlideModal);
    document.body.appendChild(modal);

    return modal;
}

function openAiExplSlideModal(sourceCard) {
    if (!sourceCard) return;
    if (document.getElementById('srs-rating-modal')?.classList.contains('active')) return;

    const modal = ensureAiExplSlideModal();
    const modalContent = modal.querySelector('.ai-expl-slide-modal-content');
    if (!modalContent) return;

    modalContent.innerHTML = '';
    const clone = sourceCard.cloneNode(true);
    clone.classList.remove('ai-expl-slide-target');
    clone.querySelectorAll('.ai-expl-slide-expand-btn').forEach((btn) => btn.remove());
    clone.querySelectorAll('.ai-expl-slide-audio-btn').forEach((btn) => btn.remove());
    clone.removeAttribute('role');
    clone.removeAttribute('tabindex');
    clone.removeAttribute('title');
    clone.setAttribute('data-testid', 'pdf-explanation-modal-slide');
    modalContent.appendChild(clone);

    modal.classList.add('active');
    document.body.classList.add('ai-expl-slide-modal-open');
    if (typeof closeQuickActionsDock === 'function') closeQuickActionsDock();
    if (typeof scheduleQuickActionsDockPosition === 'function') scheduleQuickActionsDockPosition();
}

function attachPdfExplanationSlideCards(contentRoot) {
    const formatted = contentRoot?.querySelector('.ai-formatted-response');
    if (!formatted) return;
    const voiceAgentEnabled = typeof window.isAccessibilityVoiceAgentEnabled === 'function'
        ? !!window.isAccessibilityVoiceAgentEnabled()
        : false;

    const children = Array.from(formatted.children || []);
    if (children.length === 0) return;

    const fragment = document.createDocumentFragment();
    let card = null;
    let currentSlideNumber = '';

    const extractSlideNumber = (value) => {
        const match = String(value || '').match(/\b(?:slide|page|section)\s*:?\s*(\d+)\b/i);
        return match ? String(match[1]) : '';
    };

    const createSlideCard = (slideNumber = '') => {
        card = document.createElement('article');
        card.className = 'ai-expl-slide-card';
        card.setAttribute('data-testid', 'pdf-explanation-slide-card');
        const slideCard = card;
        currentSlideNumber = slideNumber || currentSlideNumber || '';
        if (currentSlideNumber) {
            slideCard.dataset.slideNumber = currentSlideNumber;
        }
        card.title = 'Double-click to expand';
        card.addEventListener('dblclick', (event) => {
            if (event.target?.closest('button, a, input, select, textarea')) return;
            event.preventDefault();
            openAiExplSlideModal(slideCard);
        });
        const expandBtn = document.createElement('button');
        expandBtn.type = 'button';
        expandBtn.className = 'ai-expl-slide-expand-btn';
        expandBtn.setAttribute('data-testid', 'pdf-explanation-expand-btn');
        expandBtn.textContent = 'Expand';
        expandBtn.title = 'Expand this slide';
        expandBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            openAiExplSlideModal(slideCard);
        });
        if (voiceAgentEnabled) {
            const audioBtn = document.createElement('button');
            audioBtn.type = 'button';
            audioBtn.className = 'ai-expl-slide-audio-btn';
            audioBtn.setAttribute('data-testid', 'pdf-explanation-audio-btn');
            audioBtn.innerHTML = '&#128266;';
            audioBtn.title = currentSlideNumber
                ? `Read from slide ${currentSlideNumber}`
                : 'Read this explanation section';
            audioBtn.setAttribute('aria-label', audioBtn.title);
            audioBtn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                window.startAiExplanationReaderFromSlideCard?.(slideCard);
            });
            slideCard.appendChild(audioBtn);
        }
        slideCard.appendChild(expandBtn);
        fragment.appendChild(slideCard);
        return slideCard;
    };

    children.forEach((node) => {
        const isSectionHeader = node.classList && node.classList.contains('dd-section-header');
        const slideNumber = isSectionHeader ? extractSlideNumber(node.textContent) : '';
        if (!card || (slideNumber && slideNumber !== currentSlideNumber)) {
            createSlideCard(slideNumber);
        }
        card.appendChild(node);
    });

    formatted.innerHTML = '';
    formatted.appendChild(fragment);
}

function syncPdfExplanationSlideAudioButtons() {
    const voiceAgentEnabled = typeof window.isAccessibilityVoiceAgentEnabled === 'function'
        ? !!window.isAccessibilityVoiceAgentEnabled()
        : false;
    if (voiceAgentEnabled) return;
    document.querySelectorAll('.ai-expl-slide-audio-btn').forEach((node) => node.remove());
}

(function initializeModalUiSurface() {
    installGlobalDialogBackdropObserver();
    window.addEventListener('memora:accessibility-preferences-changed', syncPdfExplanationSlideAudioButtons);
})();
